import getConfig from "next/config";
import React, { useRef, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "@lib/i18n";
import { arrayOf, func, string } from "prop-types";
import { motion } from "framer-motion";

import Typography from "@components/shared/Typography";
import MapSelectionListItem from "./MapSelectionListItem";
import ArrowRightIcon from "@components/shared/Icons/ArrowRight";
import ArrowLeftIcon from "@components/shared/Icons/ArrowLeft";
import Divider from "@components/shared/Divider";

import { deliveryPointType, latLngType } from "@types";
import { getNearestPoint } from "@lib/google-maps/getNearestPoint";
import { setDeliveryDealerAction } from "@redux/actions/dealActions";
import { getStaticAssetURL } from "@lib/getStaticAssetURL";
import { useMap } from "@shared/v2/widgets/useMap";
import { useGoogleMapsWidgetSettingsSelector } from "@redux/reducers/appConfig";
import { useScrollToView } from "@hooks/useScrollToView";

import api from "@api";
import Icon from "@components/shared/Icons/Icon";
import { googleMapsStyle } from "@lib/googleMapsStyle";
import theme from "styles/theme";

const { publicRuntimeConfig } = getConfig();
const { DEAL_API_URL } = publicRuntimeConfig;

const NUMBER_OF_SHOWED_ITEMS = 3;

const DeliveryPointMap = ({
  points,
  activeDeliveryPointId,
  onDeliveryPointSelect,
  focusDeliveryAddress,
  latLng,
}) => {
  const pointRef = useRef([]);
  const targetPointIdxRef = useRef(null);
  const deliveryMapContainerRef = useRef(null);
  const scrollToView = useScrollToView();

  const { t } = useTranslation();
  const dispatch = useDispatch();
  const filterWrapRef = useRef(null);

  const { defaultCenter } = useGoogleMapsWidgetSettingsSelector();

  const [defaultMapCenter] = useState(latLng || defaultCenter);

  const [visiblePointsCount, setVisiblePointsCount] = useState(
    NUMBER_OF_SHOWED_ITEMS
  );

  const map = useMap({
    containerRef: deliveryMapContainerRef,
    options: {
      zoom: 10,
      center: defaultMapCenter,
      options: {
        disableDefaultUI: true,
      },
      styles: googleMapsStyle,
    },
  });

  const onShowMoreClick = () => {
    setVisiblePointsCount(prev => {
      targetPointIdxRef.current = prev;
      return prev + NUMBER_OF_SHOWED_ITEMS;
    });
  };

  const onShowLessClick = () => {
    setVisiblePointsCount(prev => {
      const targetIdx = Math.max(
        NUMBER_OF_SHOWED_ITEMS,
        prev - NUMBER_OF_SHOWED_ITEMS
      );
      targetPointIdxRef.current = targetIdx - NUMBER_OF_SHOWED_ITEMS;
      pointRef.current = pointRef.current.slice(0, targetIdx);
      return targetIdx;
    });
  };

  const handleMarkerClick = pointId => {
    if (!map || points.length === 0) {
      return;
    }

    const activeMarker = points.find(({ _id }) => _id === pointId);

    map.instance.panTo({
      lat: activeMarker.geoLatitude,
      lng: activeMarker.geoLongitude,
    });

    const TARGET_ZOOM = 12;
    const currentZoom = map.instance.getZoom();

    if (currentZoom < TARGET_ZOOM) {
      map.smoothZoom(TARGET_ZOOM);
    }

    focusDeliveryAddress(pointId);
    scrollToView(filterWrapRef);
  };

  useEffect(() => {
    if (!map || points.length === 0) {
      return;
    }

    const targetMarkers = points.map(point => {
      const isActive = activeDeliveryPointId === point._id;
      const iconSize = isActive ? 35 : 25;
      const iconColor = isActive
        ? theme.colors.primaryOrange
        : theme.colors.primaryDark;
      const marker = map.createMarker({
        position: {
          lat: Number(point.geoLatitude),
          lng: Number(point.geoLongitude),
        },
        icon: {
          url: `${DEAL_API_URL}/svg/logo.svg?color=${iconColor.substring(1)}`,
          scaledSize: new map.maps.Size(iconSize, iconSize),
        },
      });

      marker.addListener("click", () => {
        handleMarkerClick(point._id);
      });

      return marker;
    });

    const clusterer = map.addClusterer(targetMarkers, {
      averageCenter: true,
      enableRetinaIcons: true,
      gridSize: 50,
      imagePath: getStaticAssetURL("/images/clusterer/m", {
        withVersion: false,
      }),
    });

    const mapIdleEvent = map.maps.event.addListenerOnce(
      map.instance,
      "idle",
      () => clusterer.repaint()
    );

    return () => {
      clusterer.removeMarkers(targetMarkers);
      map.maps.event.removeListener(mapIdleEvent);
    };
  }, [map, points]);

  useEffect(() => {
    if (!map || latLng === null || points.length === 0) {
      return;
    }

    const newBoundary = new map.maps.LatLngBounds();
    getNearestPoint(points, latLng).then(nearestPoint => {
      newBoundary.extend(latLng);

      newBoundary.extend({
        lat: nearestPoint.geoLatitude,
        lng: nearestPoint.geoLongitude,
      });

      const redMarker = map.createMarker({
        position: latLng,
        icon: {
          fillColor: theme.colors.brightOrange,
          fillOpacity: 1,
          path: map.maps.SymbolPath.CIRCLE,
          scale: 9,
          strokeColor: theme.colors.primaryDark,
          strokeWeight: 1,
        },
      });

      map.render(redMarker);

      map.instance.fitBounds(newBoundary);

      return () => {
        map.remove(redMarker);
      };
    });
  }, [latLng, points.length, map]);

  useEffect(() => {
    if (
      targetPointIdxRef.current !== null &&
      pointRef.current[targetPointIdxRef.current]
    ) {
      scrollToView(pointRef.current[targetPointIdxRef.current], true);
      targetPointIdxRef.current = null;
    }
  }, [visiblePointsCount]);

  return (
    <>
      <div
        className="w-full h-125 max-h-[calc(100svh-7.5rem)] rounded-md border-2 border-greyLight"
        ref={deliveryMapContainerRef}
      />

      <Typography className="text-sm mt-8 mb-2">
        {t("basket.delivery.mapSelectionDescription")}
      </Typography>

      <div className="flex flex-col gap-2 py-4">
        {points.slice(0, visiblePointsCount).map((point, idx) => (
          <div
            key={point._id}
            ref={node => {
              if (node) {
                pointRef.current[idx] = node;
              }
            }}
          >
            <motion.div
              initial={{
                opacity: 0,
                display: "none",
              }}
              animate={{
                opacity: 1,
                display: "block",
              }}
              transition={{ duration: 0.6 }}
            >
              <MapSelectionListItem
                isActive={activeDeliveryPointId === point._id}
                onDeliveryPointClick={() => handleMarkerClick(point._id)}
                onDeliveryPointSelect={async () => {
                  await api.updateSession({ delivery: { point: point.id } });

                  dispatch(setDeliveryDealerAction(point));
                  onDeliveryPointSelect(point._id);
                }}
                point={point}
              />
            </motion.div>
          </div>
        ))}
      </div>
      <div className="flex">
        <button
          className="flex items-center gap-1 p-4 text-primaryDark hover:text-primaryOrange cursor-pointer transition duration-150 hover:scale-105 active:scale-95 disabled:text-greyLight disabled:pointer-events-none"
          disabled={visiblePointsCount <= NUMBER_OF_SHOWED_ITEMS}
          onClick={onShowLessClick}
        >
          <Icon className="size-4" icon={<ArrowLeftIcon />} />
          <span className="font-semibold uppercase text-base/tight">
            {t("basket.delivery.showLessPoints")}
          </span>
        </button>
        <Divider vertical />
        <button
          className="flex items-center gap-1 p-4 text-primaryDark hover:text-primaryOrange cursor-pointer transition duration-150 hover:scale-105 active:scale-95 disabled:text-greyLight disabled:pointer-events-none"
          disabled={visiblePointsCount >= points.length}
          onClick={onShowMoreClick}
        >
          <span className="font-semibold uppercase text-base/tight">
            {t("basket.delivery.showMorePoints")}
          </span>
          <Icon className="size-4" icon={<ArrowRightIcon />} />
        </button>
      </div>
    </>
  );
};

DeliveryPointMap.propTypes = {
  points: arrayOf(deliveryPointType).isRequired,
  onDeliveryPointSelect: func.isRequired,
  focusDeliveryAddress: func.isRequired,
  activeDeliveryPointId: string,
  latLng: latLngType,
};

export default DeliveryPointMap;
